import React, { ReactElement } from 'react';
import { atom, useAtom, Provider } from 'jotai';
import { useSetAtom } from 'jotai';
import { Role, RoleNames } from '@/generated/codegen';
import { useOptionalFilterState } from './useFilter';

type RolesGetter = (context: any) => Role[];

export const __INHERITS__ = Symbol('__INHERITS__');

const contextAtom = atom<Role[] | undefined>(undefined);
const getRolesAtom = atom<RolesGetter | undefined>(undefined);
const permissionsAtom = atom<RbacPermissions>({});

export type RbacProviderProps = {
  context: any;
  permissions: RbacPermissions;
  getRoles?: RolesGetter;
  children?: React.ReactNode;
};

export const RbacProvider: React.FC<RbacProviderProps> = ({
  context,
  getRoles = (context) => context?.roles,
  permissions,
  children,
}) => {
  return (
    <Provider>
      <RbacStateManager
        context={context}
        getRoles={getRoles}
        permissions={permissions}
      />
      {children}
    </Provider>
  );
};

type RbacStateManagerProps = RbacProviderProps;

export const RbacStateManager: React.FC<RbacStateManagerProps> = ({
  context,
  getRoles,
  permissions,
}) => {
  const memoedGetRoles = React.useMemo(
    () => getRoles || (((context) => context?.roles) as RolesGetter),
    [getRoles]
  );
  const setContext = useSetAtom(contextAtom);
  const [, setGetRoles] = useAtom(getRolesAtom);
  const setPermissions = useSetAtom(permissionsAtom);
  React.useEffect(() => {
    setContext(context);
  }, [context, setContext]);
  React.useEffect(() => {
    setGetRoles(() => memoedGetRoles);
  }, [memoedGetRoles, setGetRoles]);
  React.useEffect(() => {
    setPermissions(permissions);
  }, [permissions, setPermissions]);
  return null;
};

type PermissionPredicate =
  | (({ context, args }: { context: any; args?: any }) => boolean)
  | boolean;

type Permission = {
  [actionName: string]: PermissionPredicate;
  [__INHERITS__]?: RoleNames | RoleNames[];
};

export type RbacPermissions = Partial<Record<RoleNames | '*', Permission>>;

export function useCan(): (action: string, args?: any) => boolean;
export function useCan(action: string, args?: any): boolean;
export function useCan(
  action?: string,
  args?: any
): boolean | ((action: string, args?: any) => boolean);
export function useCan(action?: string, args?: any) {
  const [context] = useAtom(contextAtom);
  const [getRoles] = useAtom(getRolesAtom);
  const [permissions] = useAtom(permissionsAtom);
  const filter = useOptionalFilterState();

  const isAllowed = React.useCallback(
    (action: string, args?: any) =>
      can(permissions, action, undefined, context, args) || // this is in case the user has no roles at all (could match with '*')
      (getRoles?.(context)
        ?.filter((role) => {
          // if we're outside of the "context zone", just see if we have permissions
          // in any company/project
          if (!filter.query?.company_id) return true;
          // it might be a global level role (superRoot)
          if (!role.company) return true;
          // else, filter out the roles for other companies
          if (filter.query?.company_id !== role.company?.id) return false;
          // BU-scoped roles (only admin can be BU-scoped today)
          if (role.business_unit) {
            return (
              filter.value.business_unit_id === null || // company-wide view
              filter.value.business_unit_id === role.business_unit.id
            );
          }
          // else, consider the company-level roles
          if (!role.project) return true;
          // project-level role, matched against the current project (or, if
          // no project is selected, against the current BU view)
          if (filter.value.project_id !== null)
            return filter.value.project_id === role.project?.id;
          return (
            filter.value.business_unit_id === null || // "all projects" view
            role.project?.business_unit?.id === filter.value.business_unit_id // BU view
          );
        })
        .some((role) => {
          const rolename = role.role;
          return can(permissions, action, rolename, context, args);
        }) ??
        false),
    [
      context,
      filter.query?.company_id,
      filter.value.business_unit_id,
      filter.value.project_id,
      getRoles,
      permissions,
    ]
  );

  // if you don't pass an action name, you get a permission checker function
  if (action === undefined) return isAllowed;
  // otherwise we return the boolean permission for this particular action
  else return isAllowed(action, args);
}

const can = (
  permissions: RbacPermissions,
  action: string,
  rolename: RoleNames | undefined,
  context: any,
  args: any
): boolean => {
  const rolePermissions = rolename
    ? { ...permissions[rolename] }
    : { ...permissions['*'] };
  if (!rolePermissions) return false;

  // my own permissions
  const foundPermission = rolePermissions[action];
  if (typeof foundPermission === 'function')
    return foundPermission({ context, args });
  if (foundPermission === true) return true;

  // if permission is explicitly false, do not look at the inheritance chain
  if (foundPermission === false) return false;

  // inherited permissions
  const inheritedRolenames = [rolePermissions[__INHERITS__]].flatMap((a) => a);
  if (inheritedRolenames.length)
    return inheritedRolenames.some(
      (inheritedRolename) =>
        inheritedRolename &&
        can(permissions, action, inheritedRolename, context, args)
    );

  // no permissions by default
  return false;
};

export const Can: React.FC<{
  rbacAction?: string;
  rbacCheckArgs?: any;
  customMessage?: ReactElement<any>;
  children?: React.ReactNode;
}> = ({ rbacAction, rbacCheckArgs, customMessage, children }) => {
  const allowed = useCan(rbacAction, rbacCheckArgs) || !rbacAction;

  if (allowed) return <>{children}</>;
  else return customMessage ? <h1>{customMessage}</h1> : null;
};

export const useAllowedRoles = (action: string) => {
  const [context] = useAtom(contextAtom);
  const [permissions] = useAtom(permissionsAtom);

  return (Object.keys(permissions) as RoleNames[]).filter(
    (role) =>
      role !== RoleNames.SuperRoot &&
      can(permissions, action, role, context, undefined)
  );
};
